if (!customElements.get("store-locator-map")) {
class StoreLocatorMap extends HTMLElement {
  #map = null;
  #markers = [];
  #activeInfoWindow = null;
  #activeItemIndex = -1;
  #stores = [];
  #apiKey;
  #pinColor;
  #mapHeight;

  connectedCallback() {
    this.#apiKey = this.getAttribute("api-key");
    this.#pinColor = this.getAttribute("pin-color") || "#adae7f";
    this.#mapHeight = parseInt(this.getAttribute("map-height"), 10) || 550;

    try {
      const storeScript = this.querySelector('script[data-stores]');
      this.#stores = JSON.parse(storeScript?.textContent || "[]");
    } catch (e) {
      console.error("Store Locator: Invalid store data", e);
      return;
    }

    if (!this.#apiKey || this.#stores.length === 0) return;

    const list = this.querySelector(".store-locator__list");
    if (list) {
      list.style.maxHeight = this.#mapHeight + "px";
    }

    this.#loadGoogleMaps();
    this.#bindListEvents();
  }

  disconnectedCallback() {
    try {
      if (this.#map && typeof google !== "undefined") {
        google.maps.event.clearInstanceListeners(this.#map);
        for (const { marker } of this.#markers) {
          google.maps.event.clearInstanceListeners(marker);
        }
      }
    } catch (e) {
      /* Google Maps may not be loaded — fail silently */
    }
    this.#markers = [];
    this.#map = null;
    this.#activeInfoWindow = null;
  }

  async #loadGoogleMaps() {
    if (!window.googleMapsLoaded) {
      window.googleMapsLoaded = new Promise((resolve) => {
        const script = document.createElement("script");
        script.src = `https://maps.googleapis.com/maps/api/js?key=${this.#apiKey}`;
        script.onload = resolve;
        document.head.appendChild(script);
      });
    }

    await window.googleMapsLoaded;
    this.#initMap();
  }

  #initMap() {
    const mapEl = this.querySelector(".store-locator__map");
    if (!mapEl) return;

    this.#map = new google.maps.Map(mapEl, {
      center: { lat: 51.1657, lng: 10.4515 },
      zoom: 6,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: true,
      zoomControl: true,
    });

    const bounds = new google.maps.LatLngBounds();

    for (const [index, store] of this.#stores.entries()) {
      const position = {
        lat: parseFloat(store.lat),
        lng: parseFloat(store.lng),
      };
      bounds.extend(position);

      const marker = new google.maps.Marker({
        position,
        map: this.#map,
        title: store.name,
        icon: this.#createPinIcon(this.#pinColor),
      });

      const infoWindow = new google.maps.InfoWindow({
        content: this.#buildInfoWindowContent(store),
      });

      marker.addListener("click", () => {
        this.#activateStore(index, true);
      });

      this.#markers.push({ marker, infoWindow });
    }

    if (this.#stores.length > 1) {
      this.#map.fitBounds(bounds);
      google.maps.event.addListenerOnce(this.#map, "bounds_changed", () => {
        if (this.#map.getZoom() > 15) {
          this.#map.setZoom(15);
        }
      });
    } else if (this.#stores.length === 1) {
      this.#map.setCenter(bounds.getCenter());
      this.#map.setZoom(14);
    }

    this.#map.addListener("click", () => {
      this.#deactivateAll();
    });
  }

  #createPinIcon(color) {
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="42" viewBox="0 0 32 42"><path d="M16 0C7.16 0 0 7.16 0 16c0 12 16 26 16 26s16-14 16-26C32 7.16 24.84 0 16 0z" fill="${color}"/><circle cx="16" cy="16" r="6" fill="white"/></svg>`;
    return {
      url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(svg),
      scaledSize: new google.maps.Size(32, 42),
      anchor: new google.maps.Point(16, 42),
    };
  }

  #buildInfoWindowContent(store) {
    let html = `<div class="store-locator__infowindow">
      <h3 class="store-locator__infowindow-name">${this.#escapeHtml(store.name)}</h3>
      <address class="store-locator__infowindow-address">
        ${this.#escapeHtml(store.street)}<br>
        ${this.#escapeHtml(store.zip)} ${this.#escapeHtml(store.city)}
      </address>`;

    const links = [];
    if (store.phone) {
      links.push(`<a href="tel:${this.#escapeHtml(store.phone)}" class="store-locator__infowindow-link">${this.#escapeHtml(store.phone)}</a>`);
    }
    if (store.website) {
      links.push(`<a href="${this.#escapeHtml(store.website)}" target="_blank" rel="noopener" class="store-locator__infowindow-link">Website &rarr;</a>`);
    }
    if (links.length > 0) {
      html += `<div class="store-locator__infowindow-links">${links.join("")}</div>`;
    }

    html += "</div>";
    return html;
  }

  #escapeHtml(str) {
    if (!str) return "";
    const div = document.createElement("div");
    div.textContent = str;
    return div.innerHTML;
  }

  #bindListEvents() {
    const items = this.querySelectorAll(".store-locator__item");

    for (const item of items) {
      const index = parseInt(item.getAttribute("data-store-index"), 10);

      item.addEventListener("click", () => {
        this.#activateStore(index, true);
      });

      item.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          this.#activateStore(index, true);
        }
        if (e.key === "ArrowDown") {
          e.preventDefault();
          const next = item.nextElementSibling;
          if (next) next.focus();
        }
        if (e.key === "ArrowUp") {
          e.preventDefault();
          const prev = item.previousElementSibling;
          if (prev) prev.focus();
        }
      });
    }
  }

  #activateStore(index, panToMarker) {
    if (this.#activeInfoWindow) {
      this.#activeInfoWindow.close();
    }

    this.#deactivateAllItems();

    this.#activeItemIndex = index;
    const item = this.querySelector(`[data-store-index="${index}"]`);
    if (item) {
      item.setAttribute("aria-current", "true");
      item.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }

    const { marker, infoWindow } = this.#markers[index];
    infoWindow.open(this.#map, marker);
    this.#activeInfoWindow = infoWindow;

    if (panToMarker) {
      this.#map.panTo(marker.getPosition());
      if (this.#map.getZoom() < 12) {
        this.#map.setZoom(12);
      }
    }
  }

  #deactivateAll() {
    if (this.#activeInfoWindow) {
      this.#activeInfoWindow.close();
      this.#activeInfoWindow = null;
    }
    this.#deactivateAllItems();
    this.#activeItemIndex = -1;
  }

  #deactivateAllItems() {
    const items = this.querySelectorAll('[aria-current="true"]');
    for (const item of items) {
      item.removeAttribute("aria-current");
    }
  }
}

customElements.define("store-locator-map", StoreLocatorMap);
}
